import ImageEditor from "@Components/ImageEditor";
import GoogleMap from "@Components/Map";
import { optionsViewType } from "@Components/PortalSetings/Configuration/FormsAndFields/FormTemplate/constant.helper";
import { PlusIcon } from "@Components/StickySidebarViewer/assets/selectionIcons";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import IconComponent from "@CubeComponents/icons";
import AutoComplete from "@CubeComponents/Mui_Form/AutoComplete";
import CheckBoxComponent from "@CubeComponents/Mui_Form/CheckBox";
import DateTimeComponent from "@CubeComponents/Mui_Form/DateTimeComponent";
import RadioButton from "@CubeComponents/Mui_Form/RadioButton";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextBoxComponent";
import { getDistanceInMeters } from "@Helpers/constants";
import i18n from "@i18n";
import { Box, CircularProgress, FormControlLabel, FormGroup, RadioGroup } from "@mui/material";
import { locationService } from "@Services/dataManager";
import { convertDate } from "@Services/luxonHelper";
import { DropDownTreeComponent } from "@syncfusion/ej2-react-dropdowns";
import { getBackendUrl } from "@Utils";
import _ from "lodash";
import { formSectionType, onURLClick } from "./constants";
import UserCard from "./userCard";
import UserNameTagComponent from "@CubeComponents/html/UserNameTagComponent";
import { getFileIcon } from "@CubeComponents/constants/globalFunctions";

const { t } = i18n;

export const getMapWarnings = (warningMessage = "") => (
  <div className={`h-12 bg-system_colour_12 w-fit ps-3 gap-2 bg-opacity-30 pe-3 rounded-sg flex items-center`}>
    <span
      className={`w-5 h-5 flex items-center justify-center rounded-full text-white text-xs cursor-default bg-yellow-400`}
    >
      !
    </span>
    <span>{warningMessage}</span>
  </div>
);
export const getOverDistanceWarning = () => getMapWarnings(t("Selected location differs from captured location"));

export const getNoPermissionWarning = () =>
  getMapWarnings(t("Live location not captured since necessary permission not given"));

export const getUserCardAndChip = ({ user, p }) => {
  return p.eachField.viewType === optionsViewType.CHIP ? (
    <UserNameTagComponent
      id={user._id}
      handleDeleteIconClicked={
        p.enabled
          ? () =>
              p.removeUserFromList({
                fieldIndex: p.fieldIndex,
                sectionIndex: p.sectionIndex,
                user,
                eachField: p.eachField,
                section: p.section,
                source: p.source,
                tableRowIndex: p.tableRowIndex,
                handleCustomFieldData: p.handleCustomFieldData,
                extraParams: p.extraParams,
              })
          : null
      }
    />
  ) : (
    <UserCard
      fieldIndex={p.fieldIndex}
      sectionIndex={p.sectionIndex}
      section={p.section}
      source={p.source}
      tableRowIndex={p.tableRowIndex}
      showAnswersOnly={!p.enabled}
      handleCustomFieldData={p.handleCustomFieldData}
      extraParams={p.extraParams}
      removeUserFromList={p.removeUserFromList}
      field={p.eachField}
      id={user._id}
    />
  );
};

export const renderSingleLine = (p) => (
  <TextFieldComponent
    defaultValue={p.eachField.defaultValue}
    onChange={p.handleOnChange}
    onBlur={p.handleOnBlur}
    disabled={!p.enabled}
    size="small"
    error={!!_.get(p.formValidator, `error.${p.name}`)}
    value={p.currentAnswer.value ?? undefined}
    placeholder={p.eachField.placeholderText ?? ""}
    width={p.width}
  />
);

export const renderMultiLine = (p) => (
  <textarea
    style={{
      border: "1px solid #19204880",
      outline: "none",
      width: p.source === formSectionType.FORM ? "80%" : "100%",
      padding: "0.42rem 0 0.42rem 0.5rem",
      height: "100%",
      color: "black",
    }}
    disabled={!p.enabled}
    rows={p.multiTextRows || 5}
    key={p.name}
    name={p.name}
    placeholder={p.eachField.placeholderText ?? ""}
    defaultValue={p.eachField.defaultValue ?? p.currentAnswer.value ?? ""}
    onBlur={p.handleOnBlur}
    onChange={p.handleOnChange}
  />
);

export const renderSingleSelect = (p) => {
  if (p.eachField.viewType === optionsViewType.LIST) {
    return (
      <RadioGroup
        error={!!_.get(p.formValidator, `error.${p.name}`)}
        value={p.currentAnswer.value ?? undefined}
        defaultValue={p.eachField.defaultValue}
        onChange={(args) => {
          p.handleOnBlur(args);
          p.handleOnChange(args);
        }}
      >
        {p.eachField.answerChoices.map((choice) => (
          <FormControlLabel
            key={choice}
            disabled={!p.enabled}
            value={choice.trim()}
            control={<RadioButton />}
            label={choice}
          />
        ))}
      </RadioGroup>
    );
  }

  return (
    <AutoComplete
      disabled={!p.enabled}
      error={!!_.get(p.formValidator, `error.${p.name}`)}
      value={p.currentAnswer.value ?? undefined}
      defaultValue={p.eachField.defaultValue}
      onChange={(_, newValue) => {
        p.handleOnChange({ target: { value: newValue } });
        p.handleOnBlur({ target: { value: newValue } });
      }}
      id={p.name}
      options={p.eachField.answerChoices}
      getOptionLabel={(opt) => opt}
      sx={{ width: p.width }}
      renderInput={(params) => <TextFieldComponent {...params} placeholder={p.eachField.placeholderText ?? ""} />}
    />
  );
};

export const renderMultiSelect = (p) => {
  if (p.eachField.viewType === optionsViewType.LIST) {
    return (
      <FormGroup>
        {p.eachField.answerChoices?.map((choice) => (
          <FormControlLabel
            key={choice}
            control={
              <CheckBoxComponent
                onChange={(args) => {
                  p.handleOnBlur(args);
                  p.handleOnChange(args);
                }}
                disabled={!p.enabled}
                name={choice}
                checked={Array.isArray(p.currentAnswer.value) && p.currentAnswer.value.includes(choice)}
              />
            }
            label={choice}
          />
        ))}
      </FormGroup>
    );
  }

  return (
    <AutoComplete
      disabled={!p.enabled}
      multiple
      disableCloseOnSelect
      error={!!_.get(p.formValidator, `error.${p.name}`)}
      value={p.currentAnswer.value ?? undefined}
      defaultValue={p.eachField.defaultValue ?? []}
      getOptionLabel={(opt) => opt}
      onChange={(_, newValue) => {
        p.handleOnChange({ target: { value: newValue } });
        p.handleOnBlur({ target: { value: newValue } });
      }}
      id={p.name}
      options={p.eachField.answerChoices}
      sx={{ width: p.width }}
      renderInput={(params) => <TextFieldComponent {...params} placeholder={p.eachField.placeholderText ?? ""} />}
    />
  );
};

export const renderDateTime = (p) => {
  const min = p.eachField.minFieldRange || null;
  const max = p.eachField.maxFieldRange || null;

  let extraProps = {};
  if (p.eachField.selectionScope === "pastDateTime") extraProps.disableFuture = true;
  if (p.eachField.selectionScope === "futureDateTime") extraProps.disablePast = !p.currentAnswer.value;
  if (p.eachField.selectionScope === "rangeBased") extraProps = { minDate: min, maxDate: max };

  if (p.eachField.selectionScope === "currentDateTime") {
    return p.enabled ? (
      <Box
        className={`${
          p.currentAnswer?.value ? "cursor-not-allowed pointer-events-none" : "cursor-pointer"
        } border border-slate-400 w-1/2 h-8 flex items-center p-2`}
        onClick={(args) => {
          p.handleOnBlur(args);
          p.handleOnChange(args);
        }}
      >
        {p.currentAnswer?.value ? (
          convertDate(Number(p.currentAnswer?.value), "dateForTooltip")
        ) : (
          <span className="text-xs text-slate-500">{t("Click here to set date")}</span>
        )}
      </Box>
    ) : (
      <div className="cursor-not-allowed pointer-events-none border bg-gray-100 border-slate-400 w-1/2 h-8 flex items-center p-2">
        {p.currentAnswer?.value && convertDate(Number(p.currentAnswer?.value), "dateForTooltip")}
      </div>
    );
  }

  console.log(p.currentAnswer, "ppp");
  return (
    <DateTimeComponent
      slotProps={{ field: { clearable: true } }}
      {...extraProps}
      disabled={!p.enabled}
      sx={{ width: p.width }}
      format="DD/MM/YYYY hh:mm:a"
      error={!!_.get(p.formValidator, `error.${p.name}`)}
      defaultValue={p.eachField.defaultValue}
      placeholder={p.eachField.placeholderText ?? ""}
      value={p.currentAnswer.value ?? null}
      onChange={(args) => {
        p.handleOnBlur(args);
        p.handleOnChange(args);
      }}
    />
  );
};

export const renderLookUp = (p) => {
  if (p.eachField.lookUpAnsFieldType === "Location") {
    const selectedValue = (Array.isArray(p.currentAnswer.value) && p.currentAnswer.value.map((ans) => ans.id)) || [];
    return (
      <div>
        <DropDownTreeComponent
          enabled={p.enabled}
          width={p.width}
          showCheckBox
          placeholder={p.eachField.placeholderText ?? ""}
          allowMultiSelection={false}
          value={selectedValue}
          fields={{
            dataSource: locationService,
            value: "_id",
            text: "name",
            parentValue: "parentId",
            hasChildren: "hasChild",
            query: p.lookUpQuery,
          }}
          select={(args) => {
            p.handleOnChange(args);
            p.handleOnBlur(args);
          }}
          name={p.name}
          showClearButton={false}
        />
      </div>
    );
  }

  if (p.eachField.lookUpAnsFieldType === "Teams") {
    return (
      <AutoComplete
        disabled={!p.enabled}
        multiple
        error={!!_.get(p.formValidator, `error.${p.name}`)}
        disableCloseOnSelect
        getOptionLabel={(opt) => opt.name}
        value={p.currentAnswer.value ?? []}
        onChange={(_, newValue) => {
          p.handleOnChange(newValue);
          p.handleOnBlur(newValue);
        }}
        id={p.name}
        options={p.teamsData}
        sx={{ width: p.width }}
        renderInput={(params) => <TextFieldComponent {...params} placeholder={p.eachField.placeholderText ?? ""} />}
      />
    );
  }

  return <></>;
};

export const renderNumber = (p) => (
  <TextFieldComponent
    defaultValue={p.eachField.defaultValue}
    onChange={p.handleOnChange}
    onBlur={p.handleOnBlur}
    error={!!_.get(p.formValidator, `error.${p.name}`)}
    inputProps={{
      min: p.eachField.minFieldRange ? +p.eachField.minFieldRange : 0,
      max: p.eachField.maxFieldRange ? +p.eachField.maxFieldRange : null,
    }}
    disabled={!p.enabled}
    value={p.currentAnswer.value ?? undefined}
    placeholder={p.eachField.placeholderText ?? ""}
    width={p.width}
    type="number"
  />
);

export const renderUsers = (p) => (
  <div>
    <Box
      onClick={() =>
        p.enabled
          ? p.openAddUserModal({
              eachField: p.eachField,
              fieldIndex: p.fieldIndex,
              sectionIndex: p.sectionIndex,
              section: p.section,
              source: p.source,
              tableRowIndex: p.tableRowIndex,
              handleCustomFieldData: p.handleCustomFieldData,
              extraParams: p.extraParams,
            })
          : () => {}
      }
      className="bg-button_light h-8 w-14 rounded-3xl justify-center flex cursor-pointer items-center"
    >
      <IconComponent className={p.enabled ? "" : "opacity-50"} name="UserProfile" />
    </Box>
    <div className="flex flex-wrap gap-5 overflow-auto pt-5">
      {p.currentAnswer.value?.map((user) => getUserCardAndChip({ user, p }))}
    </div>
  </div>
);

export const renderFileUpload = (p) => (
  <div className="flex h-full w-full overflow-auto flex-col gap-5">
    <span>
      <input
        accept={p.eachField.allowedFileTypes.join(",")}
        type="file"
        id={`${p.name}`}
        onChange={(e) =>
          p.handleFileUploadChange({
            e,
            sectionId: p.sectionId,
            eachField: p.eachField,
            sectionIndex: p.sectionIndex,
            fieldIndex: p.fieldIndex,
            source: p.source,
            tableRowIndex: p.tableRowIndex,
            tableRowId: p.tableRowId,
            allowedFileTypes: p.eachField.allowedFileTypes,
            formData: p.formData,
            handleCustomFieldData: p.handleCustomFieldData,
            extraParams: p.extraParams,
          })
        }
        style={{ display: "none" }}
      />
      <ButtonComponent
        onClick={p.handleFileUploadClick}
        disabled={!p.enabled}
        buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
      >
        <span className="flex gap-1 items-center">
          <PlusIcon color={p.enabled ? "var(--primary-text)" : "var(--system-colour-37"} />
          <span>{p.eachField.placeholderText}</span>
        </span>
      </ButtonComponent>
    </span>
    <span className="flex flex-col pe-3 overflow-auto gap-4 w-full h-full">
      {p.currentAnswer.value?.map((data, index) => (
        <span
          key={`${data.name}-${index}`}
          className="flex gap-3 border-solid border-2 border-button_light w-fit pe-2 rounded-sg ps-2 items-center"
        >
          <IconComponent onClick={(e) => p.handleFileOpen(e, data)} name="openInViewer" />
          <span className="w-14 h-10 items-center flex object-contain"> {getFileIcon(data.name)}</span>
          <span>{data.name}</span>
          {p.enabled && (
            <span className="w-8">
              <IconComponent
                cssClass="cursor-pointer"
                onClick={() =>
                  p.handleRemoveUploadedFile({
                    fileData: data,
                    sectionIndex: p.sectionIndex,
                    fieldIndex: p.fieldIndex,
                    sectionId: p.sectionId,
                    questionId: p.questionId,
                    tableRowIndex: p.tableRowIndex,
                    source: p.source,
                    handleCustomFieldData: p.handleCustomFieldData,
                    extraParams: p.extraParams,
                  })
                }
                name="close"
              />
            </span>
          )}
        </span>
      ))}
      {p.isFileUploading.map(
        (data) =>
          data.uniqueId == p.name && (
            <span
              key={data.uniqueId}
              className="w-32 h-32 border-button_light p-1 items-center justify-center rounded flex border-2"
            >
              <CircularProgress />
            </span>
          ),
      )}
    </span>
  </div>
);

export const renderImage = (p) => (
  <div className="flex h-full w-full overflow-auto flex-col gap-5">
    <span>
      <input
        accept={p.eachField.allowedFileTypes.join(",")}
        type="file"
        id={`${p.name}`}
        onChange={(e) =>
          p.handleFileUploadChange({
            e,
            sectionId: p.sectionId,
            eachField: p.eachField,
            sectionIndex: p.sectionIndex,
            fieldIndex: p.fieldIndex,
            source: p.source,
            tableRowIndex: p.tableRowIndex,
            tableRowId: p.tableRowId,
            allowedFileTypes: p.eachField.allowedFileTypes,
            formData: p.formData,
            handleCustomFieldData: p.handleCustomFieldData,
            extraParams: p.extraParams,
          })
        }
        style={{ display: "none" }}
      />
      <ButtonComponent
        onClick={() => document.getElementById(`${p.name}`).click()}
        disabled={!p.enabled}
        buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
      >
        <span className="flex gap-1 items-center">
          <PlusIcon color={p.enabled ? "var(--primary-text)" : "var(--system-colour-37"} />
          <span>{p.eachField.placeholderText}</span>
        </span>
      </ButtonComponent>
    </span>
    <span className="flex flex-wrap flex-row pe-3 overflow-auto gap-4 w-full h-full">
      {p.currentAnswer.value?.map((data) => (
        <span key={data.awsKey} className="w-32 h-32 border-button_light p-1 rounded flex border-2">
          <ImageEditor
            isEditable={p.enabled}
            imageData={`${getBackendUrl()}/fileSystemServer/getAttachmentFile/${data.awsKey}`}
            onSubmitFunction={(base64EncodedImg) =>
              p.saveEditedImage({
                base64EncodedImg,
                handleCustomFieldData: p.handleCustomFieldData,
                extraParams: p.extraParams,
                awsKey: data.awsKey,
                sectionId: p.sectionId,
                questionId: p.questionId,
              })
            }
            heading={t("Edit Image")}
            onDelete={() =>
              p.handleRemoveUploadedFile({
                fileData: data,
                sectionIndex: p.sectionIndex,
                fieldIndex: p.fieldIndex,
                sectionId: p.sectionId,
                questionId: p.questionId,
                source: p.source,
                tableRowIndex: p.tableRowIndex,
                handleCustomFieldData: p.handleCustomFieldData,
              })
            }
          />
        </span>
      ))}
      {p.isFileUploading.map(
        (data) =>
          data.uniqueId == p.name && (
            <span
              key={data.uniqueId}
              className="w-32 h-32 border-button_light p-1 items-center justify-center rounded flex border-2"
            >
              <CircularProgress />
            </span>
          ),
      )}
    </span>
  </div>
);

export const renderURL = (p) => (
  <div className="flex h-full w-full overflow-auto flex-col gap-5">
    {p.currentAnswer?.value?.displayText ? (
      <span className="flex items-center gap-3">
        <Box
          onClick={() => {
            const url = onURLClick(_.get(p.currentAnswer, "value.URL", ""));
            window.open(url);
          }}
          className="text-gradient_color cursor-pointer underline underline-offset-2"
        >
          {_.get(p.currentAnswer, "value.displayText", "")}
        </Box>
        <ButtonComponent
          disabled={!p.enabled}
          onClick={() => {
            p.handleURLModalState({
              state: true,
              sectionIndex: p.sectionIndex,
              eachField: p.eachField,
              tableRowIndex: p.tableRowIndex,
              answer: p.currentAnswer,
              fieldIndex: p.fieldIndex,
              sectionId: p.sectionId,
              source: p.source,
              tableRowId: p.tableRowId,
            });
          }}
        >
          <IconComponent name="editProfile" cssClass="h-5" />
        </ButtonComponent>
      </span>
    ) : (
      <span>
        <ButtonComponent
          disabled={!p.enabled}
          onClick={() =>
            p.handleURLModalState({
              state: true,
              sectionIndex: p.sectionIndex,
              eachField: p.eachField,
              tableRowIndex: p.tableRowIndex,
              answer: p.currentAnswer,
              fieldIndex: p.fieldIndex,
              sectionId: p.sectionId,
              source: p.source,
              tableRowId: p.tableRowId,
            })
          }
          buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
        >
          <span className="flex gap-1 items-center">
            <IconComponent name="URL" cssClass={`h-5 ${p.enabled ? "" : "opacity-50"}`} />
            <span>{t("Enter URL")}</span>
          </span>
        </ButtonComponent>
      </span>
    )}
  </div>
);

export const renderGeoLocation = (p) => {
  const distance = getDistanceInMeters({
    pos1: p.currentAnswer.value?.current,
    pos2: p.currentAnswer.value?.selected,
  });
  return p.currentAnswer.value ? (
    <div className="flex-col flex gap-5">
      <div className="flex h-80 w-120 border flex-col gap-5 border-button_light p-3 rounded-sg">
        <GoogleMap
          allowManualSelection={false}
          allowPanning={false}
          allowEditing={false}
          autoFetchLiveLocation={false}
          showLiveLocation={!!p.currentAnswer.value?.current}
          zoomLevel={p.currentAnswer.value?.mapZoomLevel}
          defaultCenter={p.currentAnswer.value?.current}
          additionalMarker={p.currentAnswer.value?.selected}
          allowSearch={false}
        />
        {distance > 500 && getOverDistanceWarning()}
        {p.eachField?.captureLiveLocation && !p.currentAnswer.value?.current && getNoPermissionWarning()}
      </div>
      <div>
        <ButtonComponent disabled={!p.enabled} onClick={p.handleRemoveLocation}>
          <span className="flex gap-1 items-center">
            <IconComponent className={p.enabled ? "" : "opacity-50"} name="delete" />
            {t("Remove")}
          </span>
        </ButtonComponent>
      </div>
    </div>
  ) : (
    <ButtonComponent
      onClick={() =>
        p.handleGeoLocationModalState({
          sectionIndex: p.sectionIndex,
          eachField: p.eachField,
          tableRowIndex: p.tableRowIndex,
          answer: p.currentAnswer,
          fieldIndex: p.fieldIndex,
          sectionId: p.sectionId,
          source: p.source,
          tableRowId: p.tableRowId,
          state: true,
        })
      }
      disabled={!p.enabled}
      buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
    >
      <span className="flex gap-1 items-center">
        <IconComponent className={p.enabled ? "" : "opacity-50"} name="add" />
        <span>{p.eachField.placeholderText}</span>
      </span>
    </ButtonComponent>
  );
};

export const fieldRenderers = {
  "single-line": renderSingleLine,
  "multi-line": renderMultiLine,
  "single-select": renderSingleSelect,
  "multi-select": renderMultiSelect,
  "date-time": renderDateTime,
  "look-up": renderLookUp,
  number: renderNumber,
  users: renderUsers,
  fileUpload: renderFileUpload,
  image: renderImage,
  url: renderURL,
  geoLocation: renderGeoLocation,
};
